import React from 'react';
import { Check, X, Users, BookOpen, CreditCard, TrendingUp, Shield, Star } from 'lucide-react';

const MoodleAdministration: React.FC = () => {
  const serviceAreas = [
    {
      icon: <Users size={32} />,
      title: "Administración de usuarios y credenciales",
      description: "Creación y suspensión de usuarios, gestión de roles y permisos personalizados, recuperación segura de credenciales, control de accesos y auditoría.",
      benefit: "Seguridad y control total sobre quién accede a la plataforma, sin riesgo de pérdida de información."
    },
    {
      icon: <BookOpen size={32} />,
      title: "Creación y gestión de cursos",
      description: "Configuración completa de cursos, carga de contenidos y actividades, organización por categorías, automatización de procesos y plantillas estandarizadas.",
      benefit: "Cursos estructurados profesionalmente, listos para ser utilizados por estudiantes y docentes."
    },
    {
      icon: <CreditCard size={32} />,
      title: "Integración con procesos de matrícula y pagos",
      description: "Sistema de pagos en línea vinculado con Moodle, automatización de matrículas, integración con Webpay/PayPal, reportes financieros en tiempo real.",
      benefit: "Flujo sencillo para estudiantes y administración financiera eficiente para la institución."
    },
    {
      icon: <TrendingUp size={32} />,
      title: "Marketing y crecimiento estratégico",
      description: "Estrategias de marketing digital, campañas en redes sociales, optimización SEO, diseño de piezas digitales e integración con analítica.",
      benefit: "Mayor alcance, visibilidad y captación de nuevos estudiantes, aumentando las matrículas."
    },
    {
      icon: <Shield size={32} />,
      title: "Monitoreo, soporte y optimización",
      description: "Monitoreo constante, respaldos periódicos, actualizaciones programadas, soporte técnico prioritario y reportes mensuales de estado.",
      benefit: "Plataforma estable y actualizada, con soporte inmediato ante cualquier incidencia."
    }
  ];

  const plans = [
    {
      name: "Básico",
      price: "$350.000",
      period: "/mes",
      description: "Administración esencial para instituciones pequeñas",
      popular: false,
      features: [
        { text: "Gestión de usuarios y credenciales", included: true },
        { text: "Creación y configuración de cursos", included: true },
        { text: "Soporte reactivo (a solicitud)", included: true },
        { text: "Soporte proactivo (monitoreo constante)", included: false },
        { text: "Integración de pagos", included: false },
        { text: "Estrategia y asesoría en marketing", included: false },
        { text: "Reportes básicos", included: true }
      ],
      buttonText: "Contratar Básico",
      buttonClass: "btn-secondary"
    },
    {
      name: "Pro",
      price: "$650.000",
      period: "/mes",
      description: "Plan recomendado para instituciones en crecimiento",
      popular: true,
      features: [
        { text: "Gestión de usuarios y credenciales", included: true },
        { text: "Creación y configuración de cursos", included: true },
        { text: "Soporte reactivo y proactivo", included: true },
        { text: "Monitoreo constante", included: true },
        { text: "Integración de pagos", included: true },
        { text: "Asesoría básica en marketing", included: true },
        { text: "Reportes detallados", included: true }
      ],
      buttonText: "Más Popular",
      buttonClass: "btn-primary"
    },
    {
      name: "Premium",
      price: "$950.000",
      period: "/mes",
      description: "Solución completa para grandes instituciones",
      popular: false,
      features: [
        { text: "Gestión completa de usuarios y credenciales", included: true },
        { text: "Creación y configuración avanzada de cursos", included: true },
        { text: "Soporte prioritario 24/7", included: true },
        { text: "Monitoreo y optimización continua", included: true },
        { text: "Integración completa de pagos", included: true },
        { text: "Campañas completas de marketing", included: true },
        { text: "Reportes personalizados", included: true }
      ],
      buttonText: "Contratar Premium",
      buttonClass: "btn-secondary"
    }
  ];

  const benefits = [
    {
      icon: <Shield size={24} />,
      title: "Seguridad",
      description: "Control de accesos y protección de datos"
    },
    {
      icon: <TrendingUp size={24} />,
      title: "Escalabilidad",
      description: "Preparación para crecer en cursos y usuarios"
    },
    {
      icon: <Users size={24} />,
      title: "Eficiencia",
      description: "Procesos automatizados que reducen la carga administrativa"
    },
    {
      icon: <CreditCard size={24} />,
      title: "Rentabilidad",
      description: "Estrategias de marketing que aumentan las matrículas"
    }
  ];

  return (
    <section id="administracion" className="moodle-admin">
      {/* Header */}
      <div className="container">
        <div className="section-header">
          <h2>Servicios de Administración Moodle</h2>
          <p className="section-subtitle">
            Gestión integral de su plataforma e-learning para que su institución 
            se enfoque en la enseñanza mientras nosotros nos encargamos de la tecnología
          </p>
        </div>

        {/* Introduction */}
        <div className="intro-content">
          <div className="intro-text">
            <h3>Una plataforma e-learning bien gestionada es clave para el éxito educativo</h3>
            <p>
              Con nuestra propuesta buscamos ofrecer un servicio integral de administración 
              de su plataforma Moodle, que cubra la operación técnica y estratégica, 
              asegurando que su institución pueda enfocarse en la enseñanza y el crecimiento.
            </p>
            <p>
              Nuestro objetivo es simplificar la gestión, automatizar procesos y potenciar 
              el crecimiento de su oferta educativa mediante herramientas tecnológicas y 
              estrategias digitales.
            </p>
          </div>
        </div>

        {/* Service Areas */}
        <div className="service-areas">
          <h3>Alcance del servicio</h3>
          <p className="service-areas-subtitle">
            El servicio abarca cinco áreas fundamentales:
          </p>
          
          <div className="service-grid">
            {serviceAreas.map((area, index) => (
              <div key={index} className="service-card">
                <div className="service-icon">
                  {area.icon}
                </div>
                <h4>{area.title}</h4>
                <p className="service-description">{area.description}</p>
                <div className="service-benefit">
                  <strong>Beneficio:</strong> {area.benefit}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Pricing Plans */}
        <div className="pricing-section">
          <h3>Modalidades de servicio</h3>
          <p className="pricing-subtitle">
            Tres planes que se adaptan a distintas necesidades:
          </p>
          
          <div className="pricing-grid">
            {plans.map((plan, index) => (
              <div key={index} className={`pricing-card ${plan.popular ? 'popular' : ''}`}>
                {plan.popular && (
                  <div className="popular-badge">
                    <Star size={16} />
                    Recomendado
                  </div>
                )}
                
                <div className="pricing-header">
                  <h4>{plan.name}</h4>
                  <div className="price">
                    <span className="amount">{plan.price}</span>
                    <span className="period">{plan.period}</span>
                  </div>
                  <p className="plan-description">{plan.description}</p>
                </div>

                <div className="features-list">
                  {plan.features.map((feature, featureIndex) => (
                    <div key={featureIndex} className="feature-item">
                      {feature.included ? (
                        <Check size={20} className="feature-check" />
                      ) : (
                        <X size={20} className="feature-x" />
                      )}
                      <span className={feature.included ? '' : 'feature-disabled'}>
                        {feature.text}
                      </span>
                    </div>
                  ))}
                </div>

                <button className={`btn ${plan.buttonClass}`}>
                  {plan.buttonText}
                </button>
              </div>
            ))}
          </div>

          <div className="pricing-note">
            <p>
              ⚠️ <strong>Nota importante:</strong> El presupuesto publicitario de campañas de marketing 
              no está incluido y se cotiza aparte según inversión requerida.
            </p>
          </div>
        </div>

        {/* Recommended Plan Details */}
        <div className="recommended-plan">
          <h3>Plan recomendado: Pro - $650.000 CLP/mes</h3>
          <p>Este plan se adapta perfectamente a instituciones que buscan:</p>
          
          <div className="recommended-features">
            <div className="recommended-feature">
              <Check size={20} />
              <span>Administración total de usuarios y credenciales</span>
            </div>
            <div className="recommended-feature">
              <Check size={20} />
              <span>Creación de cursos y soporte en carga de contenidos</span>
            </div>
            <div className="recommended-feature">
              <Check size={20} />
              <span>Integración con sistema de pagos para matrículas automáticas</span>
            </div>
            <div className="recommended-feature">
              <Check size={20} />
              <span>Monitoreo constante y respaldo de la plataforma</span>
            </div>
            <div className="recommended-feature">
              <Check size={20} />
              <span>Asesoría en marketing digital y estrategias de captación</span>
            </div>
            <div className="recommended-feature">
              <Check size={20} />
              <span>Reporte mensual con métricas de uso y progreso</span>
            </div>
          </div>
          
          <p className="recommended-ideal">
            <strong>Ideal para:</strong> Instituciones que necesitan estabilidad, 
            automatización y crecimiento.
          </p>
        </div>

        {/* Benefits */}
        <div className="benefits-section">
          <h3>Beneficios clave para su institución</h3>
          
          <div className="benefits-grid">
            {benefits.map((benefit, index) => (
              <div key={index} className="benefit-card">
                <div className="benefit-icon">
                  {benefit.icon}
                </div>
                <h4>{benefit.title}</h4>
                <p>{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Next Steps */}
        <div className="next-steps">
          <h3>Próximos pasos</h3>
          <div className="steps-list">
            <div className="step">
              <div className="step-number">1</div>
              <p>Confirmar el plan de servicio que mejor se ajuste a sus necesidades</p>
            </div>
            <div className="step">
              <div className="step-number">2</div>
              <p>Coordinar reunión de inicio para definir procesos, accesos y responsables</p>
            </div>
            <div className="step">
              <div className="step-number">3</div>
              <p>Firmar acuerdo de servicio y comenzar la implementación</p>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="cta-section">
          <h3>¿Listo para optimizar su plataforma Moodle?</h3>
          <p>Contáctenos para una consulta personalizada y descubra cómo podemos impulsar su institución.</p>
          <a href="#contacto" className="btn btn-primary btn-lg">
            Solicitar Consulta Gratuita
          </a>
        </div>
      </div>
    </section>
  );
};

export default MoodleAdministration;